<template>
  <v-container class="px-0 pt-0" fluid grid-list-md>
    <v-row>
      <v-col v-for="(card, index) in cardsInfo" :key="index" cols="6">
        <cbm-card
          :number-text="card.value"
          :title="card.text"
          :tooltip="card.helpText"
          :boolean-loader="loadingMetrics"
          :quick-action="card.quickAction"
          :percent-text="card.underText"
          :percent-description="card.description"
          unset-percentage-colors
          simple-card
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { QUERY_GET_FUTURE_INACTIVE_CLIENTS, QUERY_GET_SALES_ESTIMATION_CARDS } from '../../graphql'
import { criteriaGroup, isInSegmentCriteria, willPurchaseCriteria, willBecomeInactiveCriteria } from '@/components/quick-actions/segmentationGroups.js'
import { DateTime } from 'luxon'
import { mapState } from 'vuex'
import { localeString } from '@/config/helpers.js'

export default {
  name: 'MariaCards',
  components: {
    CbmCard: () => import('@/components/CbmCard.vue'),
  },
  props: {
    updateCards: {
      type: Boolean,
      required: true,
    },
    totalClient: {
      type: Number,
      default: 0,
    },
    filters: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    inactiveLoading: false,
    clientsLoading: false,
    futureInactiveClients: 0,
    clientsQuantity: 0,
  }),
  computed: {
    ...mapState({
      lastUpdate: (state) => state.lastUpdate,
    }),
    percentualOfClients() {
      return (this.clientsQuantity * 100) / this.totalClient
    },
    filterDatesWithDemo() {
      if (this.$isOnTestOrDemoAccount()) {
        return [
          DateTime.fromFormat(String(this.lastUpdate.last_updated), 'yyyyMMdd').toFormat('yyyy-MM-dd'),
          DateTime.fromFormat(String(this.lastUpdate.last_updated), 'yyyyMMdd').plus({ days: +7 }).toFormat('yyyy-MM-dd'),
        ]
      }
      return this.filters.date
    },
    cardsInfo() {
      const [strStartDate, strEndDate] = this.filters.date.map((date) => DateTime.fromFormat(date, 'yyyy-MM-dd').toFormat('dd/MM/yyyy'))
      const [startDate, endDate] = this.filters.date.map((date) => date.replace(/[^0-9]/g, ''))
      return [
        {
          text: 'Clientes que vão comprar',
          value: localeString(this.clientsQuantity || 0),
          helpText: 'Clientes pertencentes aos segmentos selecionados que vão realizar ao menos uma compra prevista, na data escolhida',
          underText: `${localeString(this.percentualOfClients || 0, 1)}%`,
          description: 'do total',
          booleanLoader: this.clientsLoading,
          quickAction: {
            id: 'forecasting-will-purchase-any-products',
            shortDescription: `Clientes que vão comprar no período ${strStartDate} - ${strEndDate}`,
            longDescription: `Clientes que vão comprar no período ${strStartDate} - ${strEndDate}`,
            segmentationGroup: () => {
              return criteriaGroup([
                willPurchaseCriteria(this.filters.unitsConfiguration, this.filters.minQntPurchaseDays, startDate, endDate),
                ...this.filters.selectedSegments.map((segment) => isInSegmentCriteria(segment)),
              ])
            },
          },
        },
        {
          text: 'Futuros inativos',
          value: this.futureInactiveClients,
          helpText: 'Clientes pertencentes aos segmentos selecionados que vão deixar de realizar uma compra prevista, na data escolhida',
          booleanLoader: this.inactiveLoading,
          quickAction: {
            id: 'forecasting-will-become-inactive',
            shortDescription: `Clientes que vão se tornar inativos no período ${strStartDate} - ${strEndDate}`,
            longDescription: `Clientes que vão se tornar inativos no período ${strStartDate} - ${strEndDate}`,
            segmentationGroup: () =>
              criteriaGroup([
                willBecomeInactiveCriteria(this.filters.unitsConfiguration, this.filters.minQntPurchaseDays, startDate, endDate),
                ...this.filters.selectedSegments.map((segment) => isInSegmentCriteria(segment)),
              ]),
          },
        },
      ]
    },
  },
  watch: {
    updateCards(value) {
      if (value) {
        this.searchSalesEstimationCards()
        this.searchFutureInactiveClientsCard()
      }
    },
  },
  mounted() {
    this.searchFutureInactiveClientsCard()
    this.searchSalesEstimationCards()
  },
  methods: {
    searchFutureInactiveClientsCard() {
      this.inactiveLoading = true
      this.$apollo
        .query({
          query: QUERY_GET_FUTURE_INACTIVE_CLIENTS,
          variables: {
            segments_ids: this.filters.selectedSegments.map((segment) => segment._id),
            initial_date: this.filterDatesWithDemo[0],
            final_date: this.filterDatesWithDemo[1],
            min_qnt_purchase_days: Number(this.filters.minQntPurchaseDays),
            units: this.filters.units,
          },
          fetchPolicy: 'no-cache',
        })
        .then(({ data: { futureInactiveClientsCard } }) => {
          this.inactiveLoading = false
          this.futureInactiveClients = futureInactiveClientsCard.futureInactiveClients
        })
        .catch(() => {
          this.$snackbar({ message: 'Não existem dados a serem exibidos', snackbarColor: 'error' })
        })
    },
    searchSalesEstimationCards() {
      this.clientsLoading = true
      this.$apollo
        .query({
          query: QUERY_GET_SALES_ESTIMATION_CARDS,
          variables: {
            segments_ids: this.filters.selectedSegments.map((segment) => segment._id),
            initial_date: this.filterDatesWithDemo[0],
            final_date: this.filterDatesWithDemo[1],
            min_qnt_purchase_days: Number(this.filters.minQntPurchaseDays),
            units: this.filters.units,
          },
          fetchPolicy: 'no-cache',
        })
        .then(({ data: { salesEstimationCards } }) => {
          this.clientsLoading = false
          this.clientsQuantity = salesEstimationCards.clientsQuantity
        })
        .catch(() => {
          this.$snackbar({ message: 'Não existem dados a serem exibidos', snackbarColor: 'error' })
        })
    },
  },
}
</script>
